
/**
 * Returns the trim cut length that was actually reserved on the bar for a
 * non-Swiss reservation, derived from the stored reserved_length.
 *
 * Why derive it instead of using required_trim_cut_length directly?
 * Per PTB-5904, existing reservations must not be affected. The trim column
 * defaults to 0.5 in the DB, so pre-feature reservations carry a non-zero
 * required_trim_cut_length even though their reserved_length never included a
 * trim. Reading the trim back out of reserved_length keeps those reservations
 * unchanged (embedded ≈ 0) while still accounting for the trim on reservations
 * created after the feature shipped.
 *
 * reserved_length (new)          = (cut + kerf) * material_cut_quantity + trim
 * reserved_length (pre-feature)  = (cut + kerf) * material_cut_quantity
 *
 * For whole-piece reservations reserved_length is the entire bar (far larger
 * than cuts + trim), so the embedded value is capped at the configured trim.
 */
function getReservedTrimLength(
	cut_length: number,
	saw_kerf: number,
	reserved_length?: number,
	material_cut_quantity?: number,
	required_trim_cut_length?: number | string | null
): number {
	if (!reserved_length || !material_cut_quantity) return 0;

	const fullCuts = (cut_length + saw_kerf) * material_cut_quantity;
	const embedded = reserved_length - fullCuts;

	// Tolerance absorbs rounding noise (reserved_length is stored at 2 decimals).
	if (embedded <= 0.0001) return 0;

	const configured = Number(required_trim_cut_length ?? 0);
	return configured > 0 ? Math.min(embedded, configured) : embedded;
}

export function getPredictedRemainingLength(
	current_length: number,
	cut_length: number,
	saw_kerf: number,
	cut_quantity: number,
	production_method?: string,
	reserved_length?: number,
	required_trim_cut_length?: number | string | null,
	material_cut_quantity?: number
): string {
	const isSwiss = production_method === 'Swiss';

	let required_length = (cut_length + saw_kerf) * cut_quantity;

	if (isSwiss && reserved_length) {
		// For Swiss parts, the reserved_length represents the total length allocated (the slug).
		// We always deduct the full reserved length from the bar, regardless of how many parts are being cut from that slug.
		required_length = reserved_length;
	} else if (isSwiss) {
		// If the cut length is exactly 144.0 OR if the cut length matches the current length (within tolerance),
		// we assume this is a full bar/exact cut usage where the kerf was already accounted for
		// (e.g. reserving 12" and cutting 12").
		// In these cases, we do NOT add the saw kerf to the deduction.
		if (
			Math.abs(cut_length - 144.0) < 0.001 ||
			Math.abs(current_length - cut_length * cut_quantity) < 0.001
		) {
			required_length = cut_length * cut_quantity;
		}
	} else {
		// Non-Swiss: also deduct the one-time squaring-off trim cut that was
		// reserved on this bar so the operator does not see more remnant than
		// the bar can actually yield (and scrap/remnant classification stays
		// correct). Derived from reserved_length to leave existing reservations
		// untouched.
		required_length += getReservedTrimLength(
			cut_length,
			saw_kerf,
			reserved_length,
			material_cut_quantity,
			required_trim_cut_length
		);
	}

	return (current_length - required_length).toFixed(3);
}
